import { useState } from 'react';
import { FileText, TrendingUp } from 'lucide-react';
import { useBillingSummary } from '@/hooks/useBilling';
import { APPLIES_TO_LABELS } from '@/services/billing';
import { formatPrice } from '@/utils/format';
import BillingSummaryDetail from '@/components/BillingSummaryDetail';
import type { BillingSummaryRow } from '@/services/billing';

/**
 * Cuanto se facturo ante ARCA, abierto por punto de venta.
 *
 * Es el numero que hace falta para declarar Ingresos Brutos: las veterinarias que
 * separan servicios de productos en distintos puntos de venta necesitan el total de
 * cada uno por separado, porque los servicios estan exentos.
 *
 * No es lo mismo que "lo vendido": una venta cobrada en efectivo que no se facturo no
 * aparece aca. Por eso convive con el resto de Finanzas en vez de reemplazarlo.
 */

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

export default function BillingSummary() {
  const hoy = new Date();
  const [year, setYear] = useState(hoy.getFullYear());
  const [month, setMonth] = useState(hoy.getMonth() + 1);
  // Fila abierta: el resumen dice cuantos comprobantes hay, esto muestra cuales.
  const [detalle, setDetalle] = useState<BillingSummaryRow | null>(null);

  const { data, isLoading, isError } = useBillingSummary(year, month);

  const años = Array.from({ length: 5 }, (_, i) => hoy.getFullYear() - i);
  const filas = data?.by_point_of_sale ?? [];
  const meses = data?.by_month ?? [];
  const maxMes = Math.max(...meses.map((m) => Math.abs(Number(m.net_total))), 1);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Mes</label>
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-taupe-400"
          >
            {MESES.map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Año</label>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-taupe-400"
          >
            {años.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Cargando…</div>
      ) : isError ? (
        <div className="text-center py-8 text-gray-500">
          No se pudo cargar el resumen de facturación.
        </div>
      ) : (
        <>
          {/* Detalle del mes por punto de venta */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-400" />
              <h3 className="font-semibold text-gray-800 text-sm">
                Facturado en {MESES[month - 1]} {year}
              </h3>
            </div>

            {filas.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                No se emitieron comprobantes en este mes.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-4 py-2 text-left">Punto de venta</th>
                      <th className="px-4 py-2 text-left">Rubro</th>
                      <th className="px-4 py-2 text-right">Facturas</th>
                      <th className="px-4 py-2 text-right">Notas de crédito</th>
                      <th className="px-4 py-2 text-right">Neto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filas.map((f) => (
                      <tr
                        key={f.point_of_sale_id}
                        onClick={() => setDetalle(f)}
                        className="border-t border-gray-100 cursor-pointer hover:bg-gray-50"
                        title="Ver los comprobantes"
                      >
                        <td className="px-4 py-2">
                          <span className="font-mono font-medium text-gray-800">
                            {f.pos_number.toString().padStart(4, '0')}
                          </span>
                          <span className="text-gray-500"> · {f.description}</span>
                        </td>
                        <td className="px-4 py-2 text-gray-600">
                          {APPLIES_TO_LABELS[f.applies_to] ?? f.applies_to}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="font-medium text-gray-800">
                            {formatPrice(Number(f.invoices_total))}
                          </div>
                          <div className="text-xs text-gray-400">{f.invoices_count} emitida(s)</div>
                        </td>
                        <td className="px-4 py-2 text-right">
                          {f.credit_notes_count > 0 ? (
                            <>
                              <div className="font-medium text-red-600">
                                −{formatPrice(Number(f.credit_notes_total))}
                              </div>
                              <div className="text-xs text-gray-400">
                                {f.credit_notes_count} emitida(s)
                              </div>
                            </>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right font-bold text-gray-900">
                          {formatPrice(Number(f.net_total))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-200 bg-gray-50">
                      <td className="px-4 py-2 font-semibold text-gray-700" colSpan={4}>
                        Total facturado
                      </td>
                      <td className="px-4 py-2 text-right font-bold text-gray-900">
                        {formatPrice(Number(data?.net_total ?? 0))}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Evolucion del año */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-gray-400" />
              <h3 className="font-semibold text-gray-800 text-sm">Facturado por mes en {year}</h3>
            </div>
            <div className="p-4 space-y-1.5">
              {meses.map((m) => {
                const valor = Number(m.net_total);
                const ancho = Math.round((Math.abs(valor) / maxMes) * 100);
                const esActual = m.month === month;
                return (
                  <button
                    key={m.month}
                    type="button"
                    onClick={() => setMonth(m.month)}
                    className={`w-full flex items-center gap-3 px-2 py-1 rounded-lg text-left hover:bg-gray-50 ${
                      esActual ? 'bg-gray-50' : ''
                    }`}
                  >
                    <span
                      className={`w-24 text-xs shrink-0 ${
                        esActual ? 'font-semibold text-gray-800' : 'text-gray-500'
                      }`}
                    >
                      {MESES[m.month - 1]}
                    </span>
                    <span className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <span
                        className={`block h-full rounded-full ${
                          esActual ? 'bg-taupe-500' : 'bg-taupe-300'
                        }`}
                        style={{ width: `${ancho}%` }}
                      />
                    </span>
                    <span
                      className={`w-32 text-right text-sm shrink-0 ${
                        valor > 0 ? 'text-gray-800' : 'text-gray-300'
                      }`}
                    >
                      {valor > 0 ? formatPrice(valor) : '—'}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {detalle && (
            <BillingSummaryDetail
              row={detalle}
              year={year}
              month={month}
              monthLabel={MESES[month - 1]}
              onClose={() => setDetalle(null)}
            />
          )}

          <p className="text-xs text-gray-400">
            Tocá una fila para ver los comprobantes. Solo cuenta lo que ARCA autorizó. Una venta cobrada que no se facturó no aparece
            acá, así que este total no tiene por qué coincidir con el de ventas.
          </p>
        </>
      )}
    </div>
  );
}
